import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { ArrowRightIcon } from 'lucide-react';
interface OnboardingModalProps {
  onComplete: () => void;
}
export function OnboardingModal({
  onComplete
}: OnboardingModalProps) {
  const [step, setStep] = useState(1);
  const steps = [{
    title: 'The Situation',
    content: <div className="space-y-4" data-id="element-208">
          <p className="text-lg text-text-primary leading-relaxed" data-id="element-209">
            You are the founder of a new AI startup. You have raised a{' '}
            <span className="text-primary font-bold" data-id="element-210">$1M Seed Round</span>.
          </p>
          <p className="text-text-secondary" data-id="element-211">
            Your goal is to survive 10 years (40 quarters) and build a
            profitable, sustainable business.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-212">
            <div className="bg-surface-highlight p-4 rounded border border-border hover:border-primary/30 transition-colors" data-id="element-213">
              <div className="text-xs text-text-secondary uppercase" data-id="element-214">
                Starting Cash
              </div>
              <div className="text-xl font-mono font-bold text-success" data-id="element-215">
                $1,000,000
              </div>
            </div>
            <div className="bg-surface-highlight p-4 rounded border border-border hover:border-primary/30 transition-colors" data-id="element-216">
              <div className="text-xs text-text-secondary uppercase" data-id="element-217">
                Team Size
              </div>
              <div className="text-xl font-mono font-bold text-primary" data-id="element-218">
                6 People
              </div>
            </div>
          </div>
        </div>
  }, {
    title: 'The Mechanics',
    content: <div className="space-y-4" data-id="element-219">
          <p className="text-text-primary" data-id="element-220">
            Each turn represents one fiscal quarter. You control three levers:
          </p>
          <ul className="space-y-3 mt-4" data-id="element-221">
            <li className="flex items-start" data-id="element-222">
              <span className="bg-surface-highlight text-primary font-mono text-xs px-2 py-1 rounded mr-3 mt-0.5 border border-primary/30" data-id="element-223">
                01
              </span>
              <div data-id="element-224">
                <strong className="text-text-primary block" data-id="element-225">Pricing</strong>
                <span className="text-text-secondary text-sm" data-id="element-226">
                  Set unit price. Lower prices drive volume; higher prices drive
                  margin.
                </span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-227">
              <span className="bg-surface-highlight text-secondary font-mono text-xs px-2 py-1 rounded mr-3 mt-0.5 border border-secondary/30" data-id="element-228">
                02
              </span>
              <div data-id="element-229">
                <strong className="text-text-primary block" data-id="element-230">Hiring</strong>
                <span className="text-text-secondary text-sm" data-id="element-231">
                  Engineers build product quality. Sales reps convert demand
                  into revenue.
                </span>
              </div>
            </li>
            <li className="flex items-start" data-id="element-232">
              <span className="bg-surface-highlight text-text-primary font-mono text-xs px-2 py-1 rounded mr-3 mt-0.5 border border-border" data-id="element-233">
                03
              </span>
              <div data-id="element-234">
                <strong className="text-text-primary block" data-id="element-235">
                  Compensation
                </strong>
                <span className="text-text-secondary text-sm" data-id="element-236">
                  Pay above market to retain talent and boost quality.
                </span>
              </div>
            </li>
          </ul>
        </div>
  }, {
    title: 'Ready to Launch?',
    content: <div className="space-y-6 text-center py-4" data-id="element-237">
          <div className="w-16 h-16 bg-primary/20 text-primary rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse shadow-glow-teal" data-id="element-238">
            <ArrowRightIcon className="w-8 h-8" data-id="element-239" />
          </div>
          <p className="text-xl font-medium text-text-primary" data-id="element-240">
            The market is waiting. Your orbit begins now.
          </p>
          <p className="text-sm text-text-secondary max-w-xs mx-auto" data-id="element-241">
            Make your Q1 decisions wisely. Bankruptcy is permanent.
          </p>
        </div>
  }];
  const currentStep = steps[step - 1];
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4" data-id="element-242">
      <Card className="w-full max-w-lg shadow-2xl border-primary/20 animate-slide-up" data-id="element-243">
        <div className="flex justify-between items-center mb-6" data-id="element-244">
          <h2 className="text-xl font-bold text-text-primary" data-id="element-245">
            Briefing{' '}
            <span className="text-text-secondary font-normal text-sm ml-2" data-id="element-246">
              Step {step} of 3
            </span>
          </h2>
          <div className="flex space-x-1" data-id="element-247">
            {[1, 2, 3].map(i => <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === step ? 'bg-primary shadow-glow-teal-sm' : 'bg-surface-highlight'}`} data-id="element-248" />)}
          </div>
        </div>

        <div className="min-h-[240px]" data-id="element-249">
          <h3 className="text-lg font-medium text-text-primary mb-4" data-id="element-250">
            {currentStep.title}
          </h3>
          {currentStep.content}
        </div>

        <div className="flex justify-end mt-8 pt-4 border-t border-border" data-id="element-251">
          {step < 3 ? <Button onClick={() => setStep(step + 1)} data-id="element-252">
              Next Briefing
              <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-253" />
            </Button> : <Button onClick={onComplete} variant="primary" size="lg" data-id="element-254">
              Start Simulation
            </Button>}
        </div>
      </Card>
    </div>;
}